import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bold, Check, ChevronDown, ChevronRight, Italic, List, ListOrdered, LoaderCircle, Send, Sparkles } from 'lucide-react';

function PathwayEmailPanel({
  draftEditorRef,
  isDraftGenerating,
  chatMessages,
  chatInput,
  isChatLoading,
  models,
  selectedModel,
  reasoningEffort,
  onSelectModel,
  onChatInputChange,
  onChatSubmit,
  onChatStarter,
  onDraftEmailInput,
  onFormatDraft
}) {
  const chatStreamRef = useRef(null);
  const textareaRef = useRef(null);
  const modelMenuRef = useRef(null);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [hoveredReasoningModel, setHoveredReasoningModel] = useState('');

  useEffect(() => {
    const chatStream = chatStreamRef.current;
    if (!chatStream) return;

    if (chatMessages.length <= 1 && !isChatLoading) {
      chatStream.scrollTop = 0;
      return;
    }

    chatStream.scrollTop = chatStream.scrollHeight;
  }, [chatMessages, isChatLoading, isDraftGenerating]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 24 * 4;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [chatInput]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!modelMenuRef.current || modelMenuRef.current.contains(event.target)) return;
      setModelMenuOpen(false);
      setHoveredReasoningModel('');
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedModelDetails = models.find((item) => item.id === selectedModel);
  const modelButtonLabel = selectedModelDetails
    ? `${selectedModelDetails.label}${reasoningEffort ? ` / ${reasoningEffort}` : ''}`
    : 'Select model';
  const hasChatMessages = chatMessages.length > 0;
  const emptyAssistantMessage = isDraftGenerating
    ? 'Creating the first draft from the selected pathway options...'
    : 'I am ready to help revise this draft. Choose a quick action or type the change you want.';
  const hasPendingMessage = chatMessages.some((message) => message.isPending);
  const starterActions = [
    { label: 'Warmer', prompt: 'Make it warmer and more personal.' },
    { label: 'Shorter', prompt: 'Shorten this for a busy executive.' },
    { label: 'Clearer', prompt: 'Emphasise the alternatives clearly.' }
  ];

  const selectModel = (model, effort = '') => {
    onSelectModel(model, effort);
    setModelMenuOpen(false);
    setHoveredReasoningModel('');
  };

  return (
    <div className="pathway-email-panel">
      <div className="pathway-email-layout">
        <section className="card pathway-email-chat-panel">
          <div className="pathway-panel-header">
            <div>
              <div className="section-label">Drafting Assistant</div>
            </div>
            <Sparkles size={18} />
          </div>

          <div className="pathway-chat-card chat-card">
            {!hasChatMessages && (
              <div className="draft-assistant-ready-message chat-message-row ai">
                <div className="message-speaker">Agent</div>
                <div className={`bubble ai ${isDraftGenerating ? 'pending' : ''}`}>
                  <div className="ai-message-text markdown-body">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {emptyAssistantMessage}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            )}

            {hasChatMessages && (
              <div className="chat-stream schema-scroll" ref={chatStreamRef}>
                {chatMessages.map((message, index) => (
                <div key={message.id || `${message.role}-${index}`} className={`chat-message-row ${message.role === 'user' ? 'user' : 'ai'}`}>
                  <div className="message-speaker">{message.role === 'user' ? 'You' : 'Agent'}</div>
                  <div className={`bubble ${message.role === 'user' ? 'user' : 'ai'} ${message.isPending ? 'pending' : ''}`}>
                    {message.role === 'user' ? (
                      <div>{message.content}</div>
                    ) : (
                      <div className="ai-message-text markdown-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content || ''}
                        </ReactMarkdown>
                        {message.draftText && (
                          <div className="draft-chat-preview">
                            <div className="section-label">Updated Draft</div>
                            <pre>{message.draftText}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                ))}
                {isChatLoading && !hasPendingMessage && (
                  <div className="chat-message-row ai">
                    <div className="message-speaker">Agent</div>
                    <div className="bubble ai typing-bubble" aria-label="Assistant is typing">
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="pathway-chat-starters">
              {starterActions.map((starter) => (
                <button
                  key={starter.label}
                  type="button"
                  onClick={() => onChatStarter(starter.prompt)}
                  aria-label={starter.prompt}
                  title={starter.prompt}
                >
                  {starter.label}
                </button>
              ))}
            </div>

            <form className="chat-composer-shell" onSubmit={onChatSubmit}>
              <textarea
                ref={textareaRef}
                className="chat-textarea"
                placeholder={isDraftGenerating ? 'Type a request while the first draft is being prepared' : 'Ask for a rewrite'}
                value={chatInput}
                onChange={(event) => onChatInputChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    onChatSubmit(event);
                  }
                }}
                rows={1}
              />
              <div className="chat-composer-toolbar">
                <div className="chat-composer-left">
                  <div className="model-menu" ref={modelMenuRef}>
                    <button
                      type="button"
                      className="model-menu-trigger"
                      onClick={() => setModelMenuOpen((previous) => !previous)}
                      aria-haspopup="menu"
                      aria-expanded={modelMenuOpen}
                    >
                      <span>{modelButtonLabel}</span>
                      <ChevronDown size={14} />
                    </button>
                    {modelMenuOpen && (
                      <div className="model-menu-popover" role="menu">
                        {models.length === 0 && <div className="model-menu-empty">Loading models</div>}
                        {models.map((item) => (
                          <div
                            key={item.id}
                            className="model-menu-row"
                            onMouseEnter={() => setHoveredReasoningModel(item.id)}
                            onFocus={() => setHoveredReasoningModel(item.id)}
                          >
                            <button type="button" onClick={() => selectModel(item)}>
                              <span>{item.label}</span>
                              {selectedModel === item.id && <Check size={13} />}
                            </button>
                            {item.supportsReasoning && (
                              <div className="reasoning-flyout-wrap">
                                <button
                                  type="button"
                                  className="reasoning-flyout-trigger"
                                  aria-label={`Choose reasoning level for ${item.label}`}
                                  onMouseEnter={() => setHoveredReasoningModel(item.id)}
                                  onFocus={() => setHoveredReasoningModel(item.id)}
                                >
                                  <ChevronRight size={14} />
                                </button>
                                {hoveredReasoningModel === item.id && (
                                  <div className="reasoning-flyout">
                                    {item.reasoningEfforts.map((effort) => (
                                      <button
                                        key={`${item.id}-${effort}`}
                                        type="button"
                                        onClick={() => selectModel(item, effort)}
                                        className={selectedModel === item.id && reasoningEffort === effort ? 'active' : ''}
                                      >
                                        {effort}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="submit"
                  className="composer-send-btn"
                  disabled={!chatInput.trim()}
                  aria-label="Send"
                >
                  <Send size={18} />
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="card draft-email-panel pathway-email-draft-panel">
          <div className="recommendation-section-header">
            <div>
              <div className="section-label">Draft Message</div>
              <p className="assistant-info">
                {isDraftGenerating ? 'Generating the first draft from the selected pathway options.' : 'Edit the message directly or ask the drafting assistant to revise it.'}
              </p>
            </div>
          </div>
          <div className="draft-email-toolbar" aria-label="Draft formatting toolbar">
            <button type="button" className="icon-btn" onMouseDown={(event) => event.preventDefault()} onClick={() => onFormatDraft('bold')} aria-label="Bold">
              <Bold size={15} />
            </button>
            <button type="button" className="icon-btn" onMouseDown={(event) => event.preventDefault()} onClick={() => onFormatDraft('italic')} aria-label="Italic">
              <Italic size={15} />
            </button>
            <button type="button" className="icon-btn" onMouseDown={(event) => event.preventDefault()} onClick={() => onFormatDraft('insertUnorderedList')} aria-label="Bulleted list">
              <List size={15} />
            </button>
            <button type="button" className="icon-btn" onMouseDown={(event) => event.preventDefault()} onClick={() => onFormatDraft('insertOrderedList')} aria-label="Numbered list">
              <ListOrdered size={15} />
            </button>
          </div>
          <div className="draft-email-editor-shell">
            <div
              ref={draftEditorRef}
              className="draft-email-editor"
              role="textbox"
              aria-label="Editable draft email"
              contentEditable={!isDraftGenerating}
              suppressContentEditableWarning
              onInput={onDraftEmailInput}
            />
            {isDraftGenerating && (
              <div className="draft-email-loading" role="status" aria-live="polite" aria-label="Generating draft email">
                <LoaderCircle size={18} className="draft-email-loading-icon" aria-hidden="true" />
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default PathwayEmailPanel;
